import React, { useEffect, useState } from "react";
import "./MappingPage.css";
import { useNavigate } from "react-router-dom";

const MappingPage = ({ refreshTrigger }) => {
  const [csvData, setCsvData] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [mappingData, setMappingData] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [isAccountMatch, setIsAccountMatch] = useState(false);
  const [finalMappings, setFinalMappings] = useState({});
  const [loading, setLoading] = useState(false); 
  const [showPopup, setShowPopup] = useState(false);
  const [fileName, setFileName] = useState("updated_file.xlsx");
  const [downloadComplete, setDownloadComplete] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = localStorage.getItem("sessionId");
    if (!sessionId) {
      setSessionError("Keine Session-ID gefunden. Bitte starten Sie eine neue Sitzung.");
      return;
    }

    const fetchSessionData = async () => {
      try {
        setLoading(true);
        const response = await fetch("https://app.sijelly.com/api/session/data", {
          headers: { "Session-ID": sessionId },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setSessionError(errorData.detail || "Fehler beim Abrufen der Sitzungsdaten.");
          return;
        }

        const data = await response.json();
        if (data.csv && data.excel) {
          setCsvData(data.csv);
          setExcelData(data.excel);
          setIsAccountMatch(data.csv.konto === data.excel.account_number);
        }
      } catch (error) {
        console.error("Fehler beim Abrufen der Sitzungsdaten:", error);
        setSessionError("Netzwerkfehler: Sitzungsdaten konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [refreshTrigger]);

  useEffect(() => {
    if (mappingData) {
      setFinalMappings((prevMappings) => {
        const updatedMappings = { ...prevMappings };
  
        mappingData.excelHeaders.forEach((header) => {
          // Falls das Feld in preselectedHeaders ist und noch nicht gesetzt wurde, setzen wir "Formel"
          if (!updatedMappings[header] && preselectedHeaders.includes(header)) {
            updatedMappings[header] = "Formel";
          }
        });
  
        return updatedMappings;
      });
    }
  }, [mappingData]);

  const handleMapping = async () => {
    const sessionId = localStorage.getItem("sessionId");
    if (!sessionId) return;

    try {
      setLoading(true);
      const response = await fetch("https://app.sijelly.com/api/session/mapping", {
        method: "POST",
        headers: { "Session-ID": sessionId },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setSessionError(errorData.detail || "Fehler beim Abrufen der Zuordnungsdaten.");
        return;
      }

      const data = await response.json();
      setMappingData(data);

      const initialMappings = {};
      data.excelHeaders.forEach(header => {
        initialMappings[header] = data.csvHeaders.find(csvHeader => csvHeader === header) || "";
      });
      setFinalMappings(initialMappings);
    } catch (error) {
      console.error("Fehler beim Abrufen der Zuordnungsdaten:", error);
      setSessionError("Netzwerkfehler: Zuordnungsdaten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  const handleFinalMapping = async () => {
    const sessionId = localStorage.getItem("sessionId");
    if (!sessionId || !finalMappings) return;
  
    try {
      setLoading(true);
  
      const response = await fetch("https://app.sijelly.com/api/session/insert_data", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Session-ID": sessionId,
        },
        body: JSON.stringify(finalMappings),
      });
  
      if (!response.ok) {
        const data = await response.json();
        alert(data.detail || "Fehler beim Einfügen der Daten.");
        return;
      }
  
      const contentDisposition = response.headers.get("Content-Disposition");
      let suggestedFileName = "updated_file.xlsx";
  
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+?)"/);
        if (match && match[1]) {
          suggestedFileName = match[1];
        }
      }
  
      // ✅ Den Dateinamen setzen, bevor das Popup geöffnet wird
      setFileName(suggestedFileName);
      setShowPopup(true);
    } catch (error) {
      console.error("Fehler beim Abrufen des Dateinamens:", error);
      setSessionError("Netzwerkfehler: Datei konnte nicht vorbereitet werden.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setShowPopup(false);
  
    const sessionId = localStorage.getItem("sessionId");
    if (!sessionId || !finalMappings) return;
  
    try {
      setLoading(true);
  
      const response = await fetch("https://app.sijelly.com/api/session/insert_data", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Session-ID": sessionId,
        },
        body: JSON.stringify(finalMappings),
      });
  
      if (!response.ok) {
        const data = await response.json();
        alert(data.detail || "Fehler beim Einfügen der Daten.");
        return;
      }
  
      const blob = await response.blob();
      
      // ✅ Den Dateinamen aus dem Content-Disposition Header extrahieren
      const contentDisposition = response.headers.get("Content-Disposition");
      let suggestedFileName = "updated_file.xlsx"; // Fallback-Dateiname

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+?)"/);
        if (match && match[1]) {
          suggestedFileName = match[1];
        }
      }

      setFileName(suggestedFileName); // Setze den Dateinamen für das Popup
  
      const url = window.URL.createObjectURL(blob);
  
      // 📌 Safari & macOS: "Speichern unter"-Dialog öffnen
      const link = document.createElement("a");
      link.href = url;
      link.download = suggestedFileName;
      link.setAttribute("target", "_blank"); // ✅ Wichtig für Safari
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      alert(`Datei wurde erfolgreich heruntergeladen als: ${suggestedFileName}`);
      
      // ✅ Zeigt den "Beenden"-Button nach erfolgreichem Download an
      setDownloadComplete(true);
  
    } catch (error) {
      console.error("Fehler beim Speichern der Datei:", error);
      setSessionError("Netzwerkfehler: Datei konnte nicht gespeichert werden.");
    } finally {
      setLoading(false);
    }
};

  const preselectedHeaders = ["JJ", "MM", "TT", "Kat", "SubKat", "Kst", "Kurzname", "GKonto", "Mwst", "MwstBetrag", "Netto", "EA-Art"];


  if (sessionError) {
    return <div className="error">{sessionError}</div>;
  }
  return (
    <div className="mapping-page">
      <h1>Datenmapping Übersicht</h1>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-box">
            <div className="loading-spinner"></div>
            <p>Daten werden geladen...</p>
          </div>
        </div>
      )}

      {csvData && excelData ? (
        <>
          <div className="data-container">
            <div className="data-section">
              <h2>CSV-Daten</h2>
              <p><strong>Konto:</strong> {csvData.konto || "Nicht verfügbar"}</p>
              <p><strong>Anzahl der Zeilen:</strong> {csvData.total_rows || 0}</p>
              <p><strong>Neuestes Datum:</strong> {csvData.newest_date || "Nicht verfügbar"}</p>
              <p><strong>Ältestes Datum:</strong> {csvData.oldest_date || "Nicht verfügbar"}</p>
            </div>

            <div className="data-section">
              <h2>Excel-Daten</h2>
              <p><strong>Arbeitsblatt:</strong> {excelData.sheet_name || "Nicht verfügbar"}</p>
              <p><strong>Konto:</strong> {excelData.account_number || "Nicht verfügbar"}</p>
              <p><strong>Neuestes Datum:</strong> {excelData.newest_date || "Nicht verfügbar"}</p>
            </div>
          </div>

          <div className="comparison-section">
            {isAccountMatch ? (
              <div className="status success">✅ Kontonummern stimmen überein</div>
            ) : (
              <div className="status error">❌ Kontonummern stimmen nicht überein</div>
            )}
          </div>

          {isAccountMatch && (
            <div className="next-step">
              <button className="assign-button" onClick={handleMapping}>
                Zuordnen
              </button>
            </div>
          )}

          {mappingData && (
            <div className="mapping-fields">
              <h3>Zuordnung der Überschriften</h3>

              {mappingData.excelHeaders.map((header, index) => (
                <div className="mapping-row" key={index}>
                  <p><strong>{header}</strong></p>
                  <select
                    value={finalMappings[header] || ""}
                    onChange={(e) => setFinalMappings({ ...finalMappings, [header]: e.target.value })}
                  >
                    <option value="">-- CSV-Spalte auswählen --</option>
                    {mappingData.csvHeaders.map((csvHeader, idx) => (
                      <option key={idx} value={csvHeader}>{csvHeader}</option>
                    ))}
                  </select>
                </div>
              ))}
              <button className="finalize-button" onClick={handleFinalMapping}>
                Daten einfügen und herunterladen
              </button>{downloadComplete && (
  <button className="finish-button" onClick={() => navigate("/")}>
    Beenden
  </button>
)}
            </div>
          )}
        </>
      ) : (
        <p>Daten werden geladen...</p>
      )}

      {/* 📌 Popup-Fenster für Dateinamen */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <h2>Speicherort auswählen</h2>
            <label>
              Dateiname:
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
              />
            </label>
            <p className="safari-hint">
        <h3>💡 Falls der Speicherort nicht abgefragt wird:</h3>
        <ul>
          <li>Safari öffnen und zu den Einstellungen gehen (Cmd + ,)</li>
          <li>Tab „Allgemein“ auswählen</li>
          <li>„Dateien nach dem Download sichern in“ auf „Immer nachfragen“ ändern</li>
          <li>„Sichere Dateien nach dem Download öffnen“ deaktivieren</li>
        </ul>
        </p>
            <button className="finalize-button" onClick={handleDownload}>Speichern</button>
            <button className="finalize-button" onClick={() => setShowPopup(false)}>Abbrechen</button>
          </div>      
        </div>
        
      )}

    </div>
  );
};

export default MappingPage;